import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { WorkerShell } from "@/components/Layout";
import { House, Clock, HandGrabbing, CheckCircle, Broadcast, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * TechTurns — mobile-first "grab a vacant turn" board for field crews.
 * Open pool → tap "I'm here" to claim (optimistic FCFS).
 * In-progress → shows my active turn with a "Submit — awaiting release" button.
 */
export default function TechTurns() {
  const [board, setBoard] = useState({ open: [], in_progress: [] });
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grabbing, setGrabbing] = useState(null); // job_id being grabbed

  const load = useCallback(async () => {
    try {
      const [b, m] = await Promise.all([api.get("/turns/board"), api.get("/auth/me")]);
      setBoard(b.data || { open: [], in_progress: [] });
      setMe(m.data);
    } catch (e) {
      // silent — board is a poll, don't spam
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // 30s poll — new turns surface without a manual refresh
    return () => clearInterval(t);
  }, [load]);

  const grab = async (id) => {
    setGrabbing(id);
    try {
      await api.post(`/turns/${id}/start`);
      toast.success("You've got it. Get to work!");
      load();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("Someone beat you to it — refreshing board");
      } else {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not grab"));
      }
      load();
    } finally {
      setGrabbing(null);
    }
  };

  const finish = async (id) => {
    if (!window.confirm("Submit this turn? Office will review before releasing to the pool.")) return;
    try {
      await api.post(`/turns/${id}/finish`);
      toast.success("Submitted — office will release when ready");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not submit"));
    }
  };

  const myId = me?._id || me?.id || null;
  const mine = (board.in_progress || []).filter(j => j.turn_started_by === myId);
  const othersInProg = (board.in_progress || []).filter(j => j.turn_started_by !== myId);

  return (
    <WorkerShell>
      <div className="max-w-2xl mx-auto p-4 pb-24">
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <div className="h-12 w-12 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-lg flex items-center justify-center border-2 border-slate-900 shadow-[3px_3px_0px_#0f172a]">
            <Broadcast weight="fill" size={26} className="text-white"/>
          </div>
          <div className="flex-1">
            <div className="font-display font-black text-2xl uppercase tracking-tight leading-none">Turns Board</div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-1">
              Grab an open turn · first come, first served
            </div>
          </div>
          <button onClick={load} data-testid="turns-refresh-btn"
                  className="h-10 w-10 border-2 border-slate-900 bg-white flex items-center justify-center hover:bg-slate-100">
            <ArrowsClockwise size={18} weight="bold"/>
          </button>
        </div>

        {loading && (
          <div className="text-center py-12 text-sm font-black uppercase tracking-widest text-slate-500">
            Loading turns…
          </div>
        )}

        {/* My in-progress turns — top priority */}
        {mine.length > 0 && (
          <div className="mb-6">
            <div className="text-xs font-black uppercase tracking-widest text-emerald-700 mb-2">
              Your active turn{mine.length > 1 ? "s" : ""} · {mine.length}
            </div>
            <div className="space-y-2">
              {mine.map(j => (
                <div key={j.id} data-testid={`turns-mine-${j.id}`}
                     className="bg-white border-2 border-emerald-600 rounded-lg p-4 shadow-[4px_4px_0px_#059669]">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-emerald-500 rounded-md flex items-center justify-center flex-shrink-0">
                      <CheckCircle weight="fill" size={22} className="text-white"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-slate-900 truncate">{j.property_address || "—"}</div>
                      <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{j.title}</div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mt-1">
                        Started {j.turn_started_at ? new Date(j.turn_started_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—"}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Link
                      to={`/worker/jobs/${j.id}`}
                      className="flex-1 h-11 border-2 border-slate-900 bg-white text-slate-900 font-black uppercase tracking-widest text-xs flex items-center justify-center rounded-md hover:bg-slate-100">
                      Open WO
                    </Link>
                    <button
                      data-testid={`turns-finish-btn-${j.id}`}
                      onClick={() => finish(j.id)}
                      className="flex-1 h-11 border-2 border-slate-900 bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black uppercase tracking-widest text-xs rounded-md shadow-[3px_3px_0px_#0f172a]">
                      Submit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Open pool */}
        <div className="mb-6">
          <div className="text-xs font-black uppercase tracking-widest text-slate-700 mb-2 flex items-center gap-2">
            <HandGrabbing weight="fill" size={14}/> Open pool · {board.open?.length || 0}
          </div>
          {(!board.open || board.open.length === 0) && !loading && (
            <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              <House weight="light" size={32} className="mx-auto text-slate-400"/>
              <div className="text-sm font-bold text-slate-500 mt-2">No open turns right now</div>
              <div className="text-xs text-slate-400 mt-1">New turns will appear here automatically</div>
            </div>
          )}
          <div className="space-y-2">
            {(board.open || []).map(j => (
              <div key={j.id} data-testid={`turns-open-${j.id}`}
                   className="bg-white border-2 border-slate-900 rounded-lg p-4 shadow-[3px_3px_0px_#0f172a]">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 bg-cyan-500 rounded-md flex items-center justify-center flex-shrink-0">
                    <House weight="fill" size={22} className="text-white"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-slate-900 truncate">{j.property_address || "—"}</div>
                    <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{j.title}</div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[10px] font-black uppercase tracking-widest bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">
                        {j.source}
                      </span>
                      {j.vendor_name && (
                        <span className="text-[10px] font-black uppercase tracking-widest bg-fuchsia-100 px-1.5 py-0.5 rounded text-fuchsia-700">
                          {j.vendor_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  data-testid={`turns-grab-btn-${j.id}`}
                  onClick={() => grab(j.id)}
                  disabled={grabbing === j.id}
                  className="mt-3 w-full h-12 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 disabled:opacity-50 text-white font-display font-black uppercase tracking-widest text-sm rounded-md border-2 border-slate-900 shadow-[3px_3px_0px_#0f172a] flex items-center justify-center gap-2">
                  <HandGrabbing weight="fill" size={18}/>
                  {grabbing === j.id ? "Grabbing…" : "I'm Here · Start Work"}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Others in progress — read-only visibility */}
        {othersInProg.length > 0 && (
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
              <Clock weight="fill" size={14}/> In progress by teammates · {othersInProg.length}
            </div>
            <div className="space-y-1.5">
              {othersInProg.map(j => (
                <div key={j.id} data-testid={`turns-others-${j.id}`}
                     className="bg-slate-50 border border-slate-300 rounded p-3 flex items-center gap-3 opacity-80">
                  <div className="h-8 w-8 bg-slate-300 rounded flex items-center justify-center flex-shrink-0">
                    <Clock weight="fill" size={16} className="text-slate-600"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-800 truncate">{j.property_address}</div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {j.turn_started_by_name || "teammate"} · started {j.turn_started_at ? new Date(j.turn_started_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </WorkerShell>
  );
}
